import { XMarkIcon } from "@heroicons/react/24/solid";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Point } from "geojson";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { object, string } from "yup";
import {
    DecisionEnum,
    type Site,
    type UnmatchedDataPoint,
} from "../../apiClient/generated";
import { usePermission } from "../../auth";
import {
    useInfrastructureApiClient,
    useUnmatchedDataApiClient,
} from "../../hooks";
import { ROUTES } from "../../routes";
import { PrimaryButton, SecondaryButton } from "../../ui/Buttons";
import { AlignedDataDisplay, CopyButton } from "../../ui/GenericFields";
import { FullscreenModalBase } from "../../ui/Modals";
import { formatDateTime } from "../../utils/dates";
import {
    compassBearing,
    distanceToSite,
    formatDistance,
} from "../../utils/geopatialUtils";
import {
    invalidateKeyPrefix,
    UNMATCHED_TABLE_PREFIX,
} from "../../utils/queryInvalidation";
import { DataPointDisplay } from "../Emissions/DataPointDisplay";
import { DecisionMinimap } from "./DecisionMinimap";
import { humanizeDecision } from "./decisions";
import { SitePicker } from "./SitePicker";

// Deep link to the emission record created for a decision, on its site page.
const emissionRecordUrl = (siteId: string, emissionRecordId: string) =>
    `${ROUTES.INFRASTRUCTURE_SITE}?site=${siteId}&selectedContext=${emissionRecordId}`;

const DECISION_OPTIONS = [
    {
        value: DecisionEnum.NotOwnedByCompany,
        label: "Not owned by company",
        help: "The detection is not associated with company assets. The detection is closed and excluded from the emissions total.",
    },
    {
        value: DecisionEnum.OwnedAndLinkToSite,
        label: "Link to an existing site",
        help: "Attach the detection to a site already in AERSHED. It then appears in that site's emission event table and in all analytics.",
    },
    {
        value: DecisionEnum.OwnedButNoSite,
        label: "Site missing from AERSHED",
        help: "Flags the gap for the data team. The detection is retained and matched once the site is added.",
    },
];

const decisionOption = (decision: string | null | undefined) =>
    DECISION_OPTIONS.find((option) => option.value === decision);

// Mirrors the backend check constraint on UnmatchedDataPointDecision.
const validationSchema = object({
    decision: string().required("Pick how this detection should be handled."),
    site: string()
        .nullable()
        .when("decision", {
            is: DecisionEnum.OwnedAndLinkToSite,
            then: (schema) =>
                schema.required("Select the site this detection belongs to."),
            otherwise: (schema) => schema.nullable(),
        }),
    notes: string().nullable(),
});

const ModalCard = ({
    title,
    dataPointId,
    onClose,
    children,
    footer,
}: {
    title: string;
    dataPointId?: string;
    onClose: () => void;
    children: React.ReactNode;
    footer?: React.ReactNode;
}) => (
    <FullscreenModalBase
        visible={true}
        onClose={onClose}
        className="fixed inset-0 flex items-center justify-center p-8"
    >
        <div className="w-[1040px] max-h-[90vh] flex flex-col">
            <div className="flex-1 min-h-0 flex flex-col bg-white rounded-lg shadow-2xl overflow-hidden">
                <div className="flex items-start gap-4 px-6 py-5 border-b border-ae-slate-200">
                    <div className="flex-1 flex flex-col gap-1">
                        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                            Unmatched detection
                        </p>
                        <h1 className="text-xl font-semibold text-ae-slate-900">
                            {title}
                        </h1>
                        {!!dataPointId && (
                            <p className="flex items-center gap-1.5 text-xs text-neutral-500">
                                <span className="break-all">{dataPointId}</span>
                                <CopyButton
                                    value={dataPointId}
                                    title="Copy data point ID"
                                />
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded text-neutral-500 hover:bg-ae-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    {children}
                </div>

                {footer}
            </div>
        </div>
    </FullscreenModalBase>
);

const MapPanel = ({
    dataPoint,
    selectedSite,
    onSelectSite,
}: {
    dataPoint: UnmatchedDataPoint;
    selectedSite: Site | null;
    onSelectSite?: (siteId: string) => void;
}) => {
    const infrastructureApiClient = useInfrastructureApiClient();
    const location = dataPoint.location as Point;
    const [longitude, latitude] = location.coordinates;

    const nearestSiteQuery = useQuery({
        queryKey: ["unmatchedNearestSite", latitude, longitude],
        queryFn: async () => {
            const response =
                await infrastructureApiClient.infrastructureSitesList({
                    distanceTo: `${latitude},${longitude}`,
                    ordering: "distance",
                    pageSize: 1,
                });
            return response.results[0] ?? null;
        },
    });
    const nearestSite = nearestSiteQuery.data;

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm font-semibold text-ae-slate-900">
                    Detection location
                </p>
                {!!onSelectSite && (
                    <p className="text-xs text-neutral-500">
                        Click a site to select it
                    </p>
                )}
            </div>
            <DecisionMinimap
                mapId={`unmatched-${dataPoint.id}`}
                dataPoints={[dataPoint]}
                selectedSite={selectedSite}
                onSelectSite={onSelectSite}
            />
            {!!nearestSite && (
                <p className="text-xs leading-relaxed text-ae-slate-600">
                    Nearest AERSHED site is {nearestSite.siteName},{" "}
                    {formatDistance(distanceToSite(nearestSite, location))}{" "}
                    {nearestSite.location
                        ? compassBearing(
                              location,
                              nearestSite.location as Point,
                          )
                        : ""}{" "}
                    of the detection.
                </p>
            )}
        </div>
    );
};

const DecisionSummary = ({ dataPoint }: { dataPoint: UnmatchedDataPoint }) => {
    const infrastructureApiClient = useInfrastructureApiClient();

    // decisionSite is only a UUID, so resolve it for the name.
    const siteQuery = useQuery({
        queryKey: ["unmatchedDecisionSite", dataPoint.decisionSite],
        enabled: !!dataPoint.decisionSite,
        queryFn: async () => {
            const response =
                await infrastructureApiClient.infrastructureSitesList({
                    ids: [dataPoint.decisionSite],
                });
            return response.results[0] ?? null;
        },
    });

    const option = decisionOption(dataPoint.decision);

    return (
        <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-ae-slate-900">
                Action Taken
            </p>
            <AlignedDataDisplay
                data={[
                    {
                        label: "Action taken:",
                        value: option ? (
                            option.label
                        ) : (
                            <span className="capitalize">
                                {humanizeDecision(dataPoint.decision)}
                            </span>
                        ),
                    },
                    dataPoint.decisionSite
                        ? {
                              label: "Linked site:",
                              value:
                                  siteQuery.data?.siteName ??
                                  dataPoint.decisionSite,
                          }
                        : undefined,
                    { label: "Reported by:", value: dataPoint.decisionTakenBy },
                    {
                        label: "Date time reported:",
                        value: formatDateTime(dataPoint.decisionTakenAt),
                    },
                ].filter((row) => !!row)}
            />
            {!!option && (
                <p className="text-xs leading-relaxed text-ae-slate-600">
                    {option.help}
                </p>
            )}
        </div>
    );
};

const DecisionForm = ({
    dataPoint,
    onClose,
}: {
    dataPoint: UnmatchedDataPoint;
    onClose: () => void;
}) => {
    const apiClient = useUnmatchedDataApiClient();
    const queryClient = useQueryClient();
    const infrastructureApiClient = useInfrastructureApiClient();

    const form = useForm({
        defaultValues: { decision: undefined, site: null, notes: "" },
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
    });
    const decision = form.watch("decision");
    const siteId = form.watch("site");
    const needsSite = decision === DecisionEnum.OwnedAndLinkToSite;
    const showNotesField = decision === DecisionEnum.OwnedButNoSite;

    useEffect(() => {
        if (!showNotesField && form.getValues("notes")) {
            form.setValue("notes", "", { shouldValidate: true });
        }
    }, [showNotesField, form]);

    const selectedSiteQuery = useQuery({
        queryKey: ["unmatchedSelectedSite", siteId],
        enabled: !!siteId,
        queryFn: async () => {
            const response =
                await infrastructureApiClient.infrastructureSitesList({
                    ids: [siteId],
                });
            return response.results[0] ?? null;
        },
    });

    const saveDecision = useMutation({
        mutationFn: async (values: {
            decision: string;
            site: string | null;
            notes: string | null;
        }) =>
            apiClient.unmatchedDataDecisionsCreate({
                unmatchedDataPointDecisionRequest: {
                    dataPoint: dataPoint.id,
                    decision: values.decision as any,
                    // The field is not nullable server side, so send an empty
                    // string when the decision carries no notes.
                    notes: values.notes ?? "",
                    // Must be null, not a stale id: see the constraint above.
                    site:
                        values.decision === DecisionEnum.OwnedAndLinkToSite
                            ? values.site
                            : null,
                },
            }),
        onSuccess: (response) => {
            invalidateKeyPrefix(queryClient, UNMATCHED_TABLE_PREFIX);
            queryClient.invalidateQueries({
                queryKey: ["UnmatchedDataPoint"],
            });
            queryClient.invalidateQueries({ queryKey: ["unmatchedDataPoint"] });
            // Only decisions linking the data point to a site produce an
            // emission record to open - the others have nothing to show.
            if (response.site && response.emissionRecordId) {
                window.open(
                    emissionRecordUrl(response.site, response.emissionRecordId),
                    "_blank",
                );
            }
            onClose();
        },
        onError: async (error: any) => {
            if (error?.response?.status !== 400) {
                form.setError("root.server", {
                    type: "server",
                    message:
                        "Couldn't save the decision. Please try again or contact support.",
                });
                return;
            }
            const body = await error.response
                .text()
                .then((text: string) => {
                    try {
                        return JSON.parse(text);
                    } catch {
                        return null;
                    }
                })
                .catch(() => null);
            if (!body || typeof body !== "object") {
                form.setError("root.server", {
                    type: "server",
                    message: "Couldn't save the decision.",
                });
                return;
            }
            const toMessage = (value: unknown) =>
                Array.isArray(value) ? String(value[0]) : String(value);
            Object.entries(body).forEach(([field, messages]) => {
                // data_point and non_field_errors have no input to sit under.
                const name =
                    field === "decision" || field === "site"
                        ? field
                        : "root.server";
                form.setError(name as any, {
                    type: "server",
                    message: toMessage(messages),
                });
            });
        },
    });

    const submit = form.handleSubmit((values) => {
        // Errors from the previous attempt would otherwise stick around.
        form.clearErrors("root.server");
        return (
            saveDecision
                .mutateAsync({
                    decision: values.decision,
                    site: values.site,
                    notes: values.notes,
                })
                // `onError` already turned the failure into a form error.
                .catch(() => undefined)
        );
    });

    // Errors with no input of their own to sit under, shown in the footer.
    const serverError = form.formState.errors.root?.server?.message as
        | string
        | undefined;

    const canSave = !!decision && (!needsSite || !!siteId);
    const footerHint = canSave
        ? ""
        : needsSite
          ? "Choose the site this detection belongs to."
          : "Pick a decision to continue.";

    return (
        <form
            onSubmit={submit}
            className="flex-1 min-h-0 flex flex-col overflow-hidden"
        >
            <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-[1fr_420px] gap-6 px-6 py-5">
                <div className="flex flex-col gap-5">
                    <DataPointDisplay dataPoint={dataPoint} />

                    <div className="text-sm flex flex-col gap-2">
                        <p className="text-neutral-500">
                            How should this detection be handled?{" "}
                            <span className="text-red-500">*</span>
                        </p>
                        {DECISION_OPTIONS.map((option) => (
                            <label
                                key={option.value}
                                className="flex gap-3 items-start cursor-pointer"
                            >
                                <input
                                    type="radio"
                                    value={option.value}
                                    className="mt-1 h-4 w-4 shrink-0 accent-ae-blue-550"
                                    {...form.register("decision")}
                                />
                                <span className="flex flex-col gap-0.5">
                                    <span className="text-ae-slate-900">
                                        {option.label}
                                    </span>
                                    <span className="text-neutral-500 leading-snug">
                                        {option.help}
                                    </span>
                                </span>
                            </label>
                        ))}
                        {!!form.formState.errors.decision && (
                            <p className="text-red-500">
                                {form.formState.errors.decision.message}
                            </p>
                        )}
                    </div>

                    {needsSite && (
                        <SitePicker
                            detectionLocations={[dataPoint.location as Point]}
                            selectedSite={selectedSiteQuery.data ?? null}
                            onSelect={(site) =>
                                form.setValue("site", site.id, {
                                    shouldValidate: true,
                                })
                            }
                            error={form.formState.errors.site?.message}
                        />
                    )}

                    {showNotesField && (
                        <div className="text-sm flex flex-col gap-2">
                            <label htmlFor="notes" className="text-neutral-500">
                                Notes:
                            </label>
                            <textarea
                                id="notes"
                                rows={4}
                                className="border border-ae-slate-200 rounded p-2 text-ae-slate-900 text-sm"
                                placeholder="Add context about missing site..."
                                {...form.register("notes")}
                            />
                        </div>
                    )}
                </div>

                <MapPanel
                    dataPoint={dataPoint}
                    selectedSite={selectedSiteQuery.data ?? null}
                    onSelectSite={
                        needsSite
                            ? (id) =>
                                  form.setValue("site", id, {
                                      shouldValidate: true,
                                  })
                            : undefined
                    }
                />
            </div>

            <div className="flex items-center gap-3 px-6 py-4 border-t border-ae-slate-200 bg-ae-slate-100">
                <p
                    className={`flex-1 text-xs ${
                        serverError
                            ? "font-semibold text-red-500"
                            : "text-neutral-500"
                    }`}
                >
                    {serverError || footerHint}
                </p>
                <SecondaryButton type="button" onClick={onClose}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton
                    type="submit"
                    disabled={!canSave || form.formState.isSubmitting}
                >
                    {form.formState.isSubmitting ? "Saving…" : "Save decision"}
                </PrimaryButton>
            </div>
        </form>
    );
};

interface UnmatchedDataPointDecisionModalProps {
    dataPointId: string;
    onClose: () => void;
}

export const UnmatchedDataPointDecisionModal = (
    props: UnmatchedDataPointDecisionModalProps,
) => {
    const apiClient = useUnmatchedDataApiClient();
    const canEdit = usePermission("unmatchedEmissionEdit");

    // No retrieve-by-id endpoint exists. Fetching rather than reading the
    // clicked row keeps ?dataPoint= reloadable.
    const dataPointQuery = useQuery({
        queryKey: ["unmatchedDataPoint", props.dataPointId],
        queryFn: async () => {
            const response = await apiClient.unmatchedDataPointsList({
                ids: [props.dataPointId],
            });
            return response.results[0] ?? null;
        },
    });
    const dataPoint = dataPointQuery.data;

    if (dataPointQuery.isLoading || !dataPoint) {
        return (
            <ModalCard
                title={
                    dataPointQuery.isLoading
                        ? "Loading detection…"
                        : "Detection not found"
                }
                onClose={props.onClose}
            >
                <p className="px-6 py-8 text-sm text-neutral-500">
                    {dataPointQuery.isLoading
                        ? "Loading…"
                        : "This detection is no longer available, or you don't have access to it."}
                </p>
            </ModalCard>
        );
    }

    // The API has no update endpoint, so a taken decision can't be changed.
    const readOnly = !!dataPoint.decision || !canEdit;

    return (
        <ModalCard
            title={`${dataPoint.providerName} · ${formatDateTime(dataPoint.detectionTimestamp)}`}
            dataPointId={dataPoint.id}
            onClose={props.onClose}
            footer={
                readOnly ? (
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-ae-slate-200 bg-ae-slate-100">
                        {dataPoint.decisionSite &&
                            dataPoint.emissionRecordId && (
                                <PrimaryButton
                                    type="button"
                                    onClick={() => {
                                        window.open(
                                            emissionRecordUrl(
                                                dataPoint.decisionSite as string,
                                                dataPoint.emissionRecordId as string,
                                            ),
                                            "_blank",
                                        );
                                    }}
                                >
                                    Open emission record
                                </PrimaryButton>
                            )}
                        <SecondaryButton type="button" onClick={props.onClose}>
                            Close
                        </SecondaryButton>
                    </div>
                ) : undefined
            }
        >
            {readOnly ? (
                <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-[1fr_420px] gap-6 px-6 py-5">
                    <div className="flex flex-col gap-5">
                        <DataPointDisplay dataPoint={dataPoint} />
                        {!!dataPoint.decision && (
                            <DecisionSummary dataPoint={dataPoint} />
                        )}
                        {!dataPoint.decision && !canEdit && (
                            <p className="text-sm text-neutral-500">
                                No decision has been taken yet. You don't have
                                permission to take one.
                            </p>
                        )}
                    </div>
                    <MapPanel dataPoint={dataPoint} selectedSite={null} />
                </div>
            ) : (
                <DecisionForm dataPoint={dataPoint} onClose={props.onClose} />
            )}
        </ModalCard>
    );
};
